// Ordering the triage deck by how much a rating would actually change the answer.
//
// Nobody rates two hundred artists. The difference between forty meaningful
// ratings and two hundred meaningless ones is entirely in the order the cards come
// out, so the order is computed rather than assumed: for each unrated artist, solve
// the day with that artist pinned at the top of the scale and again at the bottom,
// and take the gap. An artist whose slot is already held by a hard lock, or whose
// window is owned by something rated far higher, moves nothing and sinks to the
// bottom of the deck where it may never be shown at all.
//
// This is the most expensive thing the client does and it is still two solves per
// artist over a state space of a hundred-odd breakpoints, which is milliseconds.

import type { TuningConfig } from './config.ts';
import { DEFAULT_CONFIG } from './config.ts';
import { solveDay } from './solver.ts';
import type { RestRequirement } from './solver.ts';
import type { Artist, DayContext, Id } from './types.ts';

export interface DecisionValueInput {
  /** One entry per festival day, already filtered to that day's sets. */
  days: DayContext[];
  /** u_full per set id, as the user's current ratings produce it. */
  utility: Map<Id, number>;
  hardLockSetIds: Id[];
  rest: Map<Id, RestRequirement[]>;
  homeStageId?: Id;
  cfg?: TuningConfig;
  /** Utility a set would carry at the top of the scale. */
  maxUtility?: number;
}

export interface DecisionValue {
  artistId: Id;
  value: number;
  /** Why it scores what it scores, for the developer panel and for trust. */
  reason: 'changes_schedule' | 'blocked_by_lock' | 'outclassed' | 'no_sets';
}

/** Scores every artist by the change a top rating versus a bottom rating makes. */
export function decisionValues(
  artists: Artist[],
  input: DecisionValueInput,
): DecisionValue[] {
  const cfg = input.cfg ?? DEFAULT_CONFIG;
  const max = input.maxUtility ?? 1;

  const setsByArtist = new Map<Id, { dayIdx: number; setId: Id }[]>();
  input.days.forEach((ctx, dayIdx) => {
    for (const s of ctx.sets) {
      const list = setsByArtist.get(s.artistId) ?? [];
      list.push({ dayIdx, setId: s.id });
      setsByArtist.set(s.artistId, list);
    }
  });

  const solveWith = (dayIdx: number, util: Map<Id, number>) =>
    solveDay({
      ctx: input.days[dayIdx],
      utility: util,
      hardLockSetIds: input.hardLockSetIds,
      rest: input.rest.get(input.days[dayIdx].day.id) ?? [],
      homeStageId: input.homeStageId,
      cfg,
    });

  const out: DecisionValue[] = [];
  for (const artist of artists) {
    const appearances = setsByArtist.get(artist.id);
    if (!appearances || appearances.length === 0) {
      out.push({ artistId: artist.id, value: 0, reason: 'no_sets' });
      continue;
    }

    let best = 0;
    let reason: DecisionValue['reason'] = 'outclassed';
    const byDay = new Map<number, Id[]>();
    for (const a of appearances) {
      byDay.set(a.dayIdx, [...(byDay.get(a.dayIdx) ?? []), a.setId]);
    }

    for (const [dayIdx, setIds] of byDay) {
      const high = new Map(input.utility);
      const low = new Map(input.utility);
      for (const id of setIds) {
        high.set(id, max);
        low.set(id, 0);
      }
      const hi = solveWith(dayIdx, high);
      const lo = solveWith(dayIdx, low);
      if (hi.infeasible || lo.infeasible) continue;
      const delta = hi.totalUtility - lo.totalUtility;
      if (delta > best) {
        best = delta;
        const attendedAtTop = hi.blocks.some((b) => b.setId && setIds.includes(b.setId));
        reason = attendedAtTop ? 'changes_schedule' : 'blocked_by_lock';
      }
    }

    out.push({ artistId: artist.id, value: best, reason: best > 1e-9 ? reason : 'outclassed' });
  }

  return out.sort((a, b) => b.value - a.value);
}

/** Returns the unrated artists worth showing, highest decision value first. */
export function triageOrder(
  artists: Artist[],
  input: DecisionValueInput,
  alreadyRated: Set<Id>,
  cutoff = 1e-6,
): { artist: Artist; value: number }[] {
  const values = new Map(decisionValues(artists, input).map((d) => [d.artistId, d.value]));
  return artists
    .filter((a) => !alreadyRated.has(a.id))
    .map((a) => ({ artist: a, value: values.get(a.id) ?? 0 }))
    .filter((x) => x.value > cutoff)
    .sort((a, b) => b.value - a.value);
}
